import type { Carer } from '@lmc/ts-common'
import {
  roleCanHoldInsightsHub,
  roleCanHoldSafeguarding
} from '../../../components/generic/teamCapabilities/capabilityToggles'
import { PermissionId } from '../../../constants/permissionIds'
import { getEffectivePermissions } from './effectivePermissions'

export type ChipId =
  | 'safeguarding'
  | 'insights-hub'
  | 'team'
  | 'service-user'
  | 'incident'
  | 'download-centre'
  | 'medication'

// Pure mapping: identifies which capability chips a carer earns. The
// user-facing label for each `id` is owned by the renderer (RolePermissionChips)
// so this util stays i18n-free (CM-3422).
export type Chip = {
  id: ChipId
}

const CHIP_DEFINITIONS: ReadonlyArray<{
  id: ChipId
  permission: (typeof PermissionId)[keyof typeof PermissionId]
}> = [
  {
    id: 'safeguarding',
    permission: PermissionId.MANAGE_SAFEGUARDING
  },
  {
    id: 'insights-hub',
    permission: PermissionId.VIEW_INSIGHTS_HUB
  },
  {
    id: 'team',
    permission: PermissionId.VIEW_AND_RESEND_TEAM_MEMBERS
  },
  {
    id: 'service-user',
    permission: PermissionId.CREATE_AND_EDIT_SERVICE_USER_DATA
  },
  {
    id: 'incident',
    permission: PermissionId.CREATE_AND_MANAGE_INCIDENTS
  },
  {
    id: 'download-centre',
    permission: PermissionId.VIEW_REPORTS_AND_EXPORT_HOME_DATA
  }
]

export const getChipsForCarer = (
  carer: Carer,
  // CM-4109: the Medication chip is only meaningful to a service with eMAR
  // enabled. Display only — `carer.medicationPermission` is untouched.
  { isEmar }: { isEmar: boolean }
): Chip[] => {
  const effective = getEffectivePermissions(carer)

  const chips: Chip[] = CHIP_DEFINITIONS.filter(({ id, permission }) => {
    if (!effective.has(permission)) {
      return false
    }
    // CM-3866: the backend strips ineligible-role capabilities at consumption
    // but leaves them on stored `permissions`, so a member downgraded from (say)
    // Admin to Senior Carer keeps a stale MANAGE_SAFEGUARDING. Suppress the
    // safeguarding chip when the member's role isn't eligible for Safeguarding
    // Oversight — matching the Edit permissions panel, which offers no toggle.
    if (id === 'safeguarding' && !roleCanHoldSafeguarding(carer.role)) {
      return false
    }
    if (id === 'insights-hub' && !roleCanHoldInsightsHub(carer.role)) {
      return false
    }
    return true
  }).map(({ id }) => ({ id }))

  if (isEmar && carer.medicationPermission === true) {
    chips.push({ id: 'medication' })
  }

  return chips
}
